import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  useTheme,
  IconButton,
  Tooltip,
  Skeleton,
  Alert,
} from '@mui/material';
import ButtonMAIQ from 'app/components/ReusableUIComponents/LowLevelComponents/buttons/ButtonMAIQ';
import { Edit, Add, Delete, Close, Save } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';
import { selectDealerSelected } from 'app/pages/Header/selectors';
import { headerSaga } from 'app/pages/Header/saga';
import { reducer, sliceKey } from 'app/pages/Header/slice';
import ModalMAIQ from 'app/components/ModalMAIQ';
import AddIcon from '@mui/icons-material/Add';
import DataGridMAIQ from 'app/components/ReusableUIComponents/HighLevelComponents/DataGridMAIQ';
import DataGridTypes from 'types/DataGridTypes';
import DealerSpecialsDetailsModal from './DealerSpecialsDetailsModal';
import DealerSpecialsService from 'app/apiService/dealerSpecials/dealerSpecialsApiService';
import { ConfirmationDialogMAIQ } from 'app/components/ReusableUIComponents/LowLevelComponents/ConfirmationDialogMAIQ';
import { parseDateValue } from 'utils/dateUtils';
import DealerSpecialForm, { DealerSpecialFormData, DealerSpecialFormErrors } from './DealerSpecialForm';

// Modal for Add/Edit Dealer Special
const DealerSpecialsModal = ({ open, onClose, initialData, onSave, isSubmitting }: any) => {
  const theme = useTheme();
  const [formData, setFormData] = useState<DealerSpecialFormData>({
    special_headline: '',
    special_description: '',
    start_date: '',
    end_date: '',
    applicable_model: [],
    disclaimer_text: '',
  });
  const [formErrors, setFormErrors] = useState<DealerSpecialFormErrors>({});

  useEffect(() => {
    if (initialData) {
      setFormData({
        special_headline: initialData.special_headline || '',
        special_description: initialData.special_description || '',
        start_date: initialData.start_date || '',
        end_date: initialData.end_date || '',
        applicable_model: Array.isArray(initialData.applicable_model)
          ? initialData.applicable_model
          : initialData.applicable_model
          ? [initialData.applicable_model]
          : [],
        disclaimer_text: initialData.disclaimer_text || '',
      });
    } else {
      setFormData({
        special_headline: '',
        special_description: '',
        start_date: '',
        end_date: '',
        applicable_model: [],
        disclaimer_text: '',
      });
    }
    setFormErrors({});
  }, [initialData, open]);

  const handleFormChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    // Clear field-specific error when user starts typing
    if (formErrors[field as keyof DealerSpecialFormErrors]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field as keyof DealerSpecialFormErrors];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const errors: DealerSpecialFormErrors = {};

    if (!formData.special_headline?.trim()) {
      errors.special_headline = 'Special headline is required';
    }
    if (!formData.special_description?.trim()) {
      errors.special_description = 'Special description is required';
    }
    if (!formData.start_date?.trim()) {
      errors.start_date = 'Start date is required';
    }
    if (!formData.end_date?.trim()) {
      errors.end_date = 'End date is required';
    }
    if (!formData.applicable_model || formData.applicable_model.length === 0) {
      errors.applicable_model = 'Applicable model is required';
    }

    const startDate = parseDateValue(formData.start_date);
    const endDate = parseDateValue(formData.end_date);

    if (!startDate || !endDate) {
      if (!startDate) errors.start_date = 'Start date is invalid';
      if (!endDate) errors.end_date = 'End date is invalid';
    } else {
      const startMillis = startDate.startOf('day').toMillis();
      const endMillis = endDate.startOf('day').toMillis();

      if (endMillis <= startMillis) {
        errors.end_date = 'End date must be after start date';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    onSave(formData);
  };

  return (
    <ModalMAIQ open={open} onClose={onClose} hideDefaultCloseButton={true}>
      <Paper
        elevation={0}
        sx={{
          width: 600,
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 0,
        }}
      >
        {/* Header - fixed */}
        <Box
          className="modal-header"
          sx={{
            p: 2,
            zIndex: 10,
            flexShrink: 0,
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AddIcon className="modal-header-icon" fontSize="small" />
              <Typography variant="h6" fontWeight="600">
                {initialData ? 'Edit Dealer Special' : 'Add New Dealer Special'}
              </Typography>
            </Box>
            <Box>
              <Tooltip title="Close">
                <IconButton
                  className="modal-header-button"
                  aria-label="close"
                  onClick={onClose}
                  sx={{
                    p: 0.5,
                  }}
                  size="small"
                >
                  <Close fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>

        {/* Scrollable content area */}
        <Box
          component="form"
          id="dealer-specials-form"
          onSubmit={handleSubmit}
          sx={{
            flex: 1,
            overflowY: 'auto',
            position: 'relative',
            py: 2,
            px: 3
          }}
        >
          {Object.keys(formErrors).length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Please fix the errors below
            </Alert>
          )}
          <DealerSpecialForm
            formData={formData}
            onFormChange={handleFormChange}
            errors={formErrors}
            disabled={isSubmitting}
          />
        </Box>

        {/* Footer action bar */}
        <Box 
          sx={{ 
            p: 2, 
            display: 'flex', 
            justifyContent: 'flex-end',
            backgroundColor: theme.palette.background.paper,
            borderTop: `1px solid ${theme.palette.divider}`,
            zIndex: 9,
            flexShrink: 0,
            gap: 2
          }}
        >
          <ButtonMAIQ
            variant="outlined"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </ButtonMAIQ>
          {isSubmitting ? (
            <Skeleton
              variant="rectangular"
              width={140}
              height={36}
              sx={{
                borderRadius: 1,
                bgcolor: theme.palette.action.hover,
              }}
            />
          ) : (
            <ButtonMAIQ
              variant="contained"
              color="primary"
              type="submit"
              form="dealer-specials-form"
              onClick={(e) => {
                // If form submission doesn't happen through the form's onSubmit,
                // trigger it manually here as a fallback
                handleSubmit(e as any);
              }}
              startIcon={<Save />}
            >
              {initialData ? 'Update Special' : 'Create Special'}
            </ButtonMAIQ>
          )}
        </Box>
      </Paper>
    </ModalMAIQ>
  );
};

interface DealerSpecialsTabProps {
  /** Optional row filter for embedding contexts (client-side filter by `_id`). */
  allowedRowIds?: string[];
  /** When true, removes outer page padding/shell so it fits in modals. */
  embedded?: boolean;
}

const DealerSpecialsTab = ({ allowedRowIds, embedded = false }: DealerSpecialsTabProps) => {
  const theme = useTheme();
  const location = useLocation();
  const selectedDealer = useSelector(selectDealerSelected) || {};
  
  // Check if we're in NewAIAvatarLayout context
  const isInAIAvatarLayout = location.pathname.startsWith('/ai-avatar-layout');
  const selectedDealerId = selectedDealer.dealer || '';

  // All hooks must be called before any early returns
  const [specials, setSpecials] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editData, setEditData] = useState<any>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedSpecial, setSelectedSpecial] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteSpecialId, setDeleteSpecialId] = useState<string>('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedRowToDelete, setSelectedRowToDelete] = useState<any>(null);
  const [gridDeleteConfirm, setGridDeleteConfirm] = useState(false);

  useInjectReducer({ key: sliceKey, reducer: reducer });
  useInjectSaga({ key: sliceKey, saga: headerSaga });

  // Check if dealer is selected - if not, show message
  if (!selectedDealerId) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh',
          color: 'text.secondary',
          fontSize: '1.5rem',
          fontWeight: 500,
        }}
      >
        <Typography variant="h4" gutterBottom component="div">
          Please select a dealer to view dealer specials
        </Typography>
      </Box>
    );
  }

  const handleAdd = () => {
    setEditData(null);
    setModalOpen(true);
  };

  const handleEdit = (row: any) => {
    setEditData(row);
    setModalOpen(true);
  };

  const handleViewDetails = (row: any) => {
    setSelectedSpecial(row);
    setDetailModalOpen(true);
  };

  const handleDelete = async (row: any) => {
    setSelectedRowToDelete(row);
    setGridDeleteConfirm(true);
  };

  const confirmGridDelete = async () => {
    if (!selectedRowToDelete) return;
    setIsDeleting(true);
    try {
      await DealerSpecialsService.deleteDealerSpecial(selectedRowToDelete._id);
      setRefreshKey(prev => prev + 1);
      setGridDeleteConfirm(false);
      setSelectedRowToDelete(null);
    } catch (err) {
      console.error('Delete dealer special error', err);
    } finally {
      setIsDeleting(false);
    }
  };

  const saveSpecial = async (id: string | null, payload: any) => {
    try {
      if (id) {
        await DealerSpecialsService.editDealerSpecial(id, payload);
      } else {
        await DealerSpecialsService.addDealerSpecial(payload);
      }
      setRefreshKey(prev => prev + 1);
    } catch (err) {
      console.error('Save dealer special error', err);
    }
  };

  const handleSave = async (data: any) => {
    setIsSubmitting(true);
    try {
      const payload = { ...data, dealership_id: selectedDealerId };
      await saveSpecial(editData?._id || null, payload);
      setModalOpen(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCellClick = (cell: any, clickedElement?: string) => {
    if (cell.field === 'actions') {
      // Handle action buttons (edit, delete)
      if (clickedElement === 'delete') {
        handleDelete(cell.row);
      } else if (clickedElement === 'edit') {
        handleEdit(cell.row);
      }
    } else if (cell.field === 'special_headline') {
      // Handle special headline click - open detail modal
      handleViewDetails(cell.row);
    }
  };

  return (
    <Box sx={{ p: embedded ? 0 : 3 }}>
      <Paper sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600 }}>
              Dealer Specials
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Manage your dealer specials and promotional offers
            </Typography>
          </Box>
          <ButtonMAIQ
            variant="contained"
            color="secondary"
            startIcon={<AddIcon />}
            onClick={handleAdd}
            sx={isInAIAvatarLayout ? {
              backgroundColor: theme.palette.mode === 'light' ? theme.palette.common.black : theme.palette.common.white,
              color: theme.palette.mode === 'light' ? theme.palette.common.white : theme.palette.common.black,
              '&:hover': {
                backgroundColor: theme.palette.mode === 'light' ? theme.palette.grey[800] : theme.palette.grey[200],
              },
            } : undefined}
          >
            Add New
          </ButtonMAIQ>
        </Box>
        <DataGridMAIQ
          type={DataGridTypes.DEALER_SPECIALS(selectedDealerId)}
          onCellClick={handleCellClick}
          allowedRowIds={allowedRowIds}
          key={refreshKey}
        />
      </Paper>
      <DealerSpecialsModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        initialData={editData}
        onSave={handleSave}
        isSubmitting={isSubmitting}
      />
      <DealerSpecialsDetailsModal
        open={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        data={selectedSpecial}
        onSave={async payload => {
          await saveSpecial(selectedSpecial?._id, { ...payload, dealership_id: selectedDealerId });
          setDetailModalOpen(false);
          setRefreshKey(prev => prev + 1);
        }}
        onDelete={async () => {
          try {
            await DealerSpecialsService.deleteDealerSpecial(selectedSpecial._id);
            setDetailModalOpen(false);
            setRefreshKey(prev => prev + 1);
          } catch (err) {
            console.error('Delete dealer special error', err);
          }
        }}
      />
      <ConfirmationDialogMAIQ
        open={gridDeleteConfirm}
        onClose={() => setGridDeleteConfirm(false)}
        onConfirm={confirmGridDelete}
        message="Are you sure you want to delete this dealer special?"
        loading={isDeleting}
      />
    </Box>
  );
};

export default DealerSpecialsTab;

export function EmbeddedDealerSpecialsTab(props: DealerSpecialsTabProps) {
  return <DealerSpecialsTab {...props} />;
}
